/**
 * The eleven-letter Simpliloquy wordmark. Each letter borrows a frequency
 * along a deliberate hue arc (warm red through magenta) so the title reads
 * as a small prism. Ported verbatim from Simpliloquy V2.
 *
 * The arc itself is the brand. The mapping is independent of the bundled
 * agent set: the wordmark stays stable even as the agent roster evolves.
 * Eleven letters means a slightly tighter step (~30.5°) than the
 * nine-letter spectrum, which keeps the gradient continuous.
 */
const SPECTRUM: Array<{ letter: string; hue: number }> = [
  { letter: 'S', hue: 15  }, // warm red
  { letter: 'i', hue: 46  }, // orange
  { letter: 'm', hue: 76  }, // amber
  { letter: 'p', hue: 106 }, // olive
  { letter: 'l', hue: 137 }, // green
  { letter: 'i', hue: 168 }, // teal-cyan
  { letter: 'l', hue: 198 }, // cyan-blue
  { letter: 'o', hue: 228 }, // blue
  { letter: 'q', hue: 259 }, // indigo
  { letter: 'u', hue: 289 }, // purple
  { letter: 'y', hue: 320 }, // magenta
];

export interface WordmarkProps {
  /** Size preset. `hero` is the Welcome screen; `nav` is the top bar. */
  size?: 'hero' | 'nav';
}

export function Wordmark({ size = 'hero' }: WordmarkProps) {
  const isHero = size === 'hero';
  return (
    <h1
      className="font-semibold tracking-tight select-none leading-none"
      style={{
        // Hero clamp is tighter than V2's (3.5-7rem) so all 11 letters
        // fit on one line at phone widths. Mark validated the smaller
        // floor on 2026-05-07; this is an intentional departure.
        fontSize: isHero ? 'clamp(1.75rem, 9vw, 5.5rem)' : '1.2rem',
        textAlign: isHero ? 'center' : 'left',
        whiteSpace: 'nowrap',
      }}
    >
      {SPECTRUM.map(({ letter, hue }, i) => (
        <Letter key={letter + i} letter={letter} hue={hue} size={size} />
      ))}
    </h1>
  );
}

function Letter({
  letter,
  hue,
  size,
}: {
  letter: string;
  hue: number;
  size: 'hero' | 'nav';
}) {
  const color = `oklch(78% 0.16 ${hue})`;
  const glow = size === 'hero'
    ? `oklch(62% 0.17 ${hue} / 0.45)`
    : `oklch(62% 0.17 ${hue} / 0.35)`;
  const shadow = size === 'hero' ? '0 2px 20px' : '0 1px 8px';
  return (
    <span
      style={{
        color,
        textShadow: `${shadow} ${glow}`,
        display: 'inline-block',
      }}
    >
      {letter}
    </span>
  );
}
